import React, { useRef } from 'react';
import { Button, useToast } from '@chakra-ui/react';
import { useUploadThing, buildUploadHeaders } from './uploadthingClient';

/**
 * Drop-in replacement for the old UploadthingWidget.
 * Props:
 *   uploadEndpoint  – Uploadthing route name (required)
 *   onUploadSuccess – called with (url, { name, size, type })
 *   customButton    – optional Chakra Button element to use as the trigger
 *   uploadHeaders   – static headers object forwarded to the upload request
 *   options         – ignored (kept so old call-sites don't crash on spread)
 */
export const UploadthingWidget = ({
	uploadEndpoint = 'employeeProfileImage',
	onUploadSuccess,
	customButton,
	uploadHeaders,
	accept,
	allowedMimeTypes,
	options, // eslint-disable-line no-unused-vars
}) => {
	const inputRef = useRef(null);
	const toast = useToast();
	const { startUpload, isUploading } = useUploadThing(uploadEndpoint, {
		headers: () =>
			buildUploadHeaders(
				typeof uploadHeaders === 'function' ? uploadHeaders() : uploadHeaders,
			),
	});

	const handleFileChange = async (e) => {
		const files = Array.from(e.target.files || []);
		if (!files.length) return;
		if (allowedMimeTypes && allowedMimeTypes.length) {
			const bad = files.find((f) => !allowedMimeTypes.includes(f.type));
			if (bad) {
				e.target.value = '';
				// eslint-disable-next-line no-alert
				alert(
					`Only the following file types are allowed: ${allowedMimeTypes.join(', ')}`,
				);
				return;
			}
		}
		try {
			const res = await startUpload(files);
			const uploaded = res?.[0];
			const url = uploaded?.ufsUrl ?? uploaded?.url;
			if (url) {
				onUploadSuccess?.(url, {
					name: uploaded?.name ?? files[0]?.name,
					size: uploaded?.size ?? files[0]?.size,
					type: files[0]?.type,
				});
			} else {
				toast({
					title: 'Upload failed',
					description: 'No file URL was returned. Please try again.',
					status: 'error',
					duration: 4000,
					isClosable: true,
				});
			}
		} catch (error) {
			toast({
				title: 'Upload failed',
				description: error?.message || 'Could not upload the file.',
				status: 'error',
				duration: 5000,
				isClosable: true,
			});
		}
		// reset so the same file can be re-selected
		e.target.value = '';
	};

	const handleClick = () => inputRef.current?.click();

	return (
		<>
			<input
				ref={inputRef}
				type='file'
				accept={accept}
				style={{ display: 'none' }}
				onChange={handleFileChange}
			/>
			{customButton ? (
				React.cloneElement(customButton, {
					onClick: handleClick,
					isLoading: isUploading || customButton.props.isLoading,
					isDisabled: isUploading || customButton.props.isDisabled,
				})
			) : (
				<Button
					onClick={handleClick}
					isLoading={isUploading}
					colorScheme='teal'
					size='sm'>
					{isUploading ? 'Uploading…' : 'Upload File'}
				</Button>
			)}
		</>
	);
};
